import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigation } from './NavigationBridge';

// Context preservation types
interface ViewState {
  viewId: string;
  data: Record<string, any>;
  scrollPosition: number;
  formData: Record<string, any>;
  selectedItems: string[];
  filters: Record<string, any>;
  lastUpdated: number;
}

interface TabState {
  id: string;
  title: string;
  view: string;
  context: Record<string, any>;
  state: ViewState;
  isActive: boolean;
  isDirty: boolean;
  createdAt: number;
  lastAccessedAt: number;
}

interface ContextPreservationState {
  viewStates: Record<string, ViewState>;
  tabs: TabState[];
  activeTabId: string | null;
  maxTabs: number;
}

interface ContextPreservationContextType {
  state: ContextPreservationState;
  preserveViewState: (viewId: string, data: Partial<ViewState>) => void;
  getViewState: (viewId: string) => ViewState | null;
  clearViewState: (viewId: string) => void;
  createTab: (title: string, view: string, context?: Record<string, any>) => string;
  closeTab: (tabId: string) => void;
  switchTab: (tabId: string) => void;
  updateTab: (tabId: string, updates: Partial<TabState>) => void;
  duplicateTab: (tabId: string) => string;
  getActiveTab: () => TabState | null;
  markTabDirty: (tabId: string, isDirty: boolean) => void;
}

// Create context
const ContextPreservationContext = createContext<ContextPreservationContextType | null>(null);

// Context preservation provider
export const ContextPreservationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ContextPreservationState>(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('abtest-context-preservation');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.warn('Failed to parse saved context preservation state');
      }
    }
    
    return {
      viewStates: {},
      tabs: [],
      activeTabId: null,
      maxTabs: 8
    };
  });

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('abtest-context-preservation', JSON.stringify(state));
  }, [state]);

  const preserveViewState = useCallback((viewId: string, data: Partial<ViewState>) => {
    setState(prev => ({
      ...prev,
      viewStates: {
        ...prev.viewStates,
        [viewId]: {
          viewId,
          data: {},
          scrollPosition: 0,
          formData: {},
          selectedItems: [],
          filters: {},
          lastUpdated: Date.now(),
          ...prev.viewStates[viewId],
          ...data
        }
      }
    }));
  }, []);

  const getViewState = useCallback((viewId: string) => {
    return state.viewStates[viewId] || null;
  }, [state.viewStates]);

  const clearViewState = useCallback((viewId: string) => {
    setState(prev => ({
      ...prev,
      viewStates: Object.fromEntries(
        Object.entries(prev.viewStates).filter(([key]) => key !== viewId)
      )
    }));
  }, []);

  const createTab = useCallback((title: string, view: string, context: Record<string, any> = {}) => {
    const tabId = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const now = Date.now();

    const newTab: TabState = {
      id: tabId,
      title,
      view,
      context,
      state: {
        viewId: view,
        data: {},
        scrollPosition: 0,
        formData: {},
        selectedItems: [],
        filters: {},
        lastUpdated: now
      },
      isActive: true,
      isDirty: false,
      createdAt: now,
      lastAccessedAt: now
    };

    setState(prev => {
      // Close oldest tab if we're at max capacity
      let updatedTabs = prev.tabs;
      if (prev.tabs.length >= prev.maxTabs) {
        const oldestTab = prev.tabs
          .filter(tab => !tab.isDirty)
          .sort((a, b) => a.lastAccessedAt - b.lastAccessedAt)[0];
        
        if (oldestTab) {
          updatedTabs = prev.tabs.filter(tab => tab.id !== oldestTab.id);
        }
      }

      return {
        ...prev,
        tabs: [
          ...updatedTabs.map(tab => ({ ...tab, isActive: false })),
          newTab
        ],
        activeTabId: tabId
      };
    });

    return tabId;
  }, []);

  const closeTab = useCallback((tabId: string) => {
    setState(prev => {
      const _tabToClose = prev.tabs.find(tab => tab.id === tabId);
      const remainingTabs = prev.tabs.filter(tab => tab.id !== tabId);
      
      let newActiveTabId = prev.activeTabId;
      
      // If we're closing the active tab, switch to another tab
      if (prev.activeTabId === tabId && remainingTabs.length > 0) {
        // Find the most recently accessed tab
        const mostRecent = remainingTabs
          .sort((a, b) => b.lastAccessedAt - a.lastAccessedAt)[0];
        newActiveTabId = mostRecent.id;
      } else if (remainingTabs.length === 0) {
        newActiveTabId = null;
      }

      return {
        ...prev,
        tabs: remainingTabs.map(tab => ({
          ...tab,
          isActive: tab.id === newActiveTabId
        })),
        activeTabId: newActiveTabId
      };
    });
  }, []);

  const switchTab = useCallback((tabId: string) => {
    setState(prev => ({
      ...prev,
      tabs: prev.tabs.map(tab => ({
        ...tab,
        isActive: tab.id === tabId,
        lastAccessedAt: tab.id === tabId ? Date.now() : tab.lastAccessedAt
      })),
      activeTabId: tabId
    }));
  }, []);

  const updateTab = useCallback((tabId: string, updates: Partial<TabState>) => {
    setState(prev => ({
      ...prev,
      tabs: prev.tabs.map(tab =>
        tab.id === tabId
          ? { ...tab, ...updates, lastAccessedAt: Date.now() }
          : tab
      )
    }));
  }, []);

  const duplicateTab = useCallback((tabId: string) => {
    const tabToDuplicate = state.tabs.find(tab => tab.id === tabId);
    if (!tabToDuplicate) return '';

    return createTab(
      `${tabToDuplicate.title} (Copy)`,
      tabToDuplicate.view,
      tabToDuplicate.context
    );
  }, [state.tabs, createTab]);

  const getActiveTab = useCallback(() => {
    return state.tabs.find(tab => tab.id === state.activeTabId) || null;
  }, [state.tabs, state.activeTabId]);

  const markTabDirty = useCallback((tabId: string, isDirty: boolean) => {
    setState(prev => ({
      ...prev,
      tabs: prev.tabs.map(tab =>
        tab.id === tabId ? { ...tab, isDirty } : tab
      )
    }));
  }, []);

  const contextValue: ContextPreservationContextType = {
    state,
    preserveViewState,
    getViewState,
    clearViewState,
    createTab,
    closeTab,
    switchTab,
    updateTab,
    duplicateTab,
    getActiveTab,
    markTabDirty
  };

  return (
    <ContextPreservationContext.Provider value={contextValue}>
      {children}
    </ContextPreservationContext.Provider>
  );
};

// Hook to use context preservation
export const useContextPreservation = () => {
  const context = useContext(ContextPreservationContext);
  if (!context) {
    throw new Error('useContextPreservation must be used within a ContextPreservationProvider');
  }
  return context;
};

// Tab bar component
export const TabBar: React.FC = () => {
  const { state, switchTab, closeTab, duplicateTab, markTabDirty: _markTabDirty } = useContextPreservation();
  const { navigateTo } = useNavigation();

  const handleTabClick = (tab: TabState) => {
    switchTab(tab.id);
    navigateTo(tab.view, tab.context);
  };

  const handleTabClose = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();
    const tab = state.tabs.find(t => t.id === tabId);
    
    if (tab?.isDirty) {
      const confirmed = window.confirm('This tab has unsaved changes. Are you sure you want to close it?');
      if (!confirmed) return;
    }
    
    closeTab(tabId);
  };

  const handleTabContextMenu = (e: React.MouseEvent, tab: TabState) => {
    e.preventDefault();
    // Show context menu with options like duplicate, close others, etc.
    // For now, just duplicate the tab
    duplicateTab(tab.id);
  };

  if (state.tabs.length === 0) return null;

  return (
    <div className="bg-surface border-b border-line">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-1 py-2 overflow-x-auto">
          {state.tabs.map((tab) => (
            <div
              key={tab.id}
              className={`
                flex items-center space-x-2 px-3 py-2 rounded-t-lg cursor-pointer transition-colors min-w-0 max-w-48
                ${tab.isActive 
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-b-2 border-blue-500' 
                  : 'text-content-secondary hover:text-content hover:bg-gray-50 dark:hover:bg-dark-700'
                }
              `}
              onClick={() => handleTabClick(tab)}
              onContextMenu={(e) => handleTabContextMenu(e, tab)}
              title={tab.title}
            >
              <div className="flex items-center space-x-2 min-w-0 flex-1">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  tab.view === 'template' ? 'bg-green-500' :
                  tab.view === 'test' ? 'bg-blue-500' :
                  tab.view === 'agent' ? 'bg-purple-500' :
                  'bg-gray-500'
                }`} />
                <span className="text-sm font-medium truncate">{tab.title}</span>
                {tab.isDirty && (
                  <div className="w-1.5 h-1.5 bg-orange-500 rounded-full flex-shrink-0" />
                )}
              </div>
              <button
                onClick={(e) => handleTabClose(e, tab.id)}
                className="flex-shrink-0 p-1 hover:bg-gray-200 dark:hover:bg-dark-600 rounded transition-colors"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
          
          {state.tabs.length < state.maxTabs && (
            <button
              onClick={() => {
                // Create a new tab - this would typically be handled by the parent component
                console.log('Create new tab');
              }}
              className="flex items-center justify-center w-8 h-8 text-content-subtle hover:text-content-secondary hover:bg-gray-100 dark:hover:bg-dark-700 rounded transition-colors"
              title="New tab"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Hook for preserving scroll position
export const useScrollPreservation = (viewId: string) => {
  const { preserveViewState, getViewState } = useContextPreservation();

  const saveScrollPosition = useCallback((position: number) => {
    preserveViewState(viewId, { scrollPosition: position });
  }, [viewId, preserveViewState]);

  const restoreScrollPosition = useCallback(() => {
    const viewState = getViewState(viewId);
    if (viewState?.scrollPosition) {
      window.scrollTo(0, viewState.scrollPosition);
    }
  }, [viewId, getViewState]);

  useEffect(() => {
    const handleScroll = () => {
      saveScrollPosition(window.scrollY);
    };

    const throttledScroll = throttle(handleScroll, 100);
    window.addEventListener('scroll', throttledScroll);

    // Restore scroll position on mount
    restoreScrollPosition();

    return () => {
      window.removeEventListener('scroll', throttledScroll);
    };
  }, [saveScrollPosition, restoreScrollPosition]);

  return { saveScrollPosition, restoreScrollPosition };
};

// Hook for preserving form data
export const useFormPreservation = (viewId: string, formId: string) => {
  const { preserveViewState, getViewState } = useContextPreservation();

  const saveFormData = useCallback((data: Record<string, any>) => {
    const viewState = getViewState(viewId);
    const updatedFormData = {
      ...viewState?.formData,
      [formId]: data
    };
    preserveViewState(viewId, { formData: updatedFormData });
  }, [viewId, formId, preserveViewState, getViewState]);

  const getFormData = useCallback(() => {
    const viewState = getViewState(viewId);
    return viewState?.formData?.[formId] || {};
  }, [viewId, formId, getViewState]);

  const clearFormData = useCallback(() => {
    const viewState = getViewState(viewId);
    if (viewState?.formData) {
      const updatedFormData = { ...viewState.formData };
      delete updatedFormData[formId];
      preserveViewState(viewId, { formData: updatedFormData });
    }
  }, [viewId, formId, preserveViewState, getViewState]);

  return { saveFormData, getFormData, clearFormData };
};

// Utility function for throttling
function throttle<T extends (...args: any[]) => any>(func: T, limit: number): T {
  let inThrottle: boolean;
  return ((...args: any[]) => {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }) as T;
}